// Public self-registration view: reachable while logged OUT (routed outside the
// auth/admin guards in App.tsx). On success the server sets the session cookie
// and returns the login shape, so the auth context refreshes /auth/me and the
// app routes into the dashboard. 403 = registration disabled, 409 = email taken.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';

export function Register() {
  const { registerWithPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // EV1 — the gateway requires e-mail confirmation: the account exists, no session yet.
  const [pending, setPending] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const out = await registerWithPassword(email.trim(), password, displayName.trim() || undefined);
      if (out.verificationRequired) {
        setPending(out.email ?? email.trim());
        return;
      }
      // Cookie/session is set; Gate now sees a user and renders the dashboard.
      navigate('/', { replace: true });
    } catch (ex) {
      if (ex instanceof ApiError && ex.status === 403) {
        setErr('Registration is disabled. Ask an administrator for an invite.');
      } else if (ex instanceof ApiError && ex.status === 409) {
        setErr('Email already registered. Try signing in instead.');
      } else {
        setErr(ex instanceof Error ? ex.message : 'registration failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand">
          <span className="brand-mark">◣◢</span>
          <span className="brand-name">mcpgraph</span>
        </div>
        <p className="login-sub">Create an account</p>

        {pending ? (
          <div className="login-form" data-testid="register-check-mail">
            <p>
              Учётная запись создана. Мы отправили письмо на <b>{pending}</b> — перейдите по ссылке в нём,
              чтобы подтвердить e-mail, затем войдите.
            </p>
          </div>
        ) : (
        <form onSubmit={submit} className="login-form">
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
          </label>
          <label>
            Display name
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" placeholder="optional" />
          </label>
          <label>
            Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
          </label>
          {err && <div className="form-error">{err}</div>}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        )}

        <p className="login-alt">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
